import { createStitches } from '@stitches/react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef } from 'react';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
var breakpoints = {
  s: 0,
  m: 600,
  l: 1025,
  xl: 1280,
  xxl: 1360
};
var {
  styled,
  theme,
  css,
  createTheme,
  getCssText,
  globalCss,
  config,
  keyframes
} = createStitches({
  utils: {
    // We are creating a utility here that automatically converts our fontsize
    // based on media queries for the components that require it (for example h1,h2)
    fontSize: (value) => {
      switch (value) {
        case "$headline-1":
          return {
            fontSize: "2.5rem",
            "@mUp": {
              fontSize: value
            }
          };
        case "$headline-2":
          return {
            fontSize: "1.75rem",
            "@mUp": {
              fontSize: value
            }
          };
      }
      return { fontSize: value };
    },
    lineHeight: (value) => {
      switch (value) {
        case 1:
          return {
            lineHeight: 1.3,
            "@mUp": {
              lineHeight: value
            }
          };
        case 2:
          return {
            lineHeight: 1.43,
            "@mUp": {
              lineHeight: value
            }
          };
      }
      return { lineHeight: value };
    }
  },
  theme: {
    colors: {
      text: "$group-blue-primary",
      background: "$white-primary",
      "button-filled-background": "$group-blue-primary",
      card: "#002b4514",
      overlay: "$group-blue-24",
      // used on: testimonials-image
      "surface-on-low-emphasis": "#002B451F",
      // used on: divider
      // GroupBlue
      "group-blue-primary": "#002B45",
      "group-blue-secondary": "#002B45A3",
      "group-blue-84": "#294D63",
      "group-blue-70": "#4C6B7D",
      "group-blue-57": "#6E8695",
      "group-blue-45": "#8C9FAB",
      "group-blue-34": "#A8B7C0",
      "group-blue-24": "#C2CCD2",
      "group-blue-15": "#D9DFE3",
      "group-blue-07": "#EEF0F1",
      "group-blue-disabled": "#002B4557",
      // Navy
      "navy-primary": "#05468C",
      "navy-secondary": "#05468CA3",
      "navy-medium": "#1E6ED7",
      "navy-disabled": "#05468C57",
      // Blue
      "blue-primary": "#3B98F1",
      "blue-secondary": "#3B98F1A3",
      "blue-medium": "#6BB7F9",
      "blue-light": "#A5D5FA",
      "blue-extra-light": "#D6EEFF",
      "blue-disabled": "#3B98F157",
      // Acqua
      "aqua-primary": "#008291",
      "aqua-medium": "#25BAC0",
      "aqua-light": "#77D8DB",
      "aqua-extra-light": "#C9EFF3",
      // Green
      "green-primary": "#2CA968",
      "green-medium": "#68C884",
      "green-light": "#A5E1B9",
      "green-extra-light": "#DBF3E3",
      // Orange
      "orange-primary": "#F76900",
      "orange-medium": "#FD9049",
      "orange-light": "#FCC694",
      "orange-extra-light": "#FFEBD7",
      // Red
      "red-primary": "#C92332",
      "red-medium": "#E15D64",
      "red-light": "#F8AAA5",
      // White
      "white-primary": "#FFFFFF",
      "white-secondary": "#FFFFFFA3",
      "white-disabled": "#FFFFFF57",
      // Divider Dark
      "divider-dark": "#4D4E4C29",
      "divider-light": "#ffffff29"
    },
    radii: {
      xs: ".125rem",
      s: ".25rem",
      m: ".5rem",
      l: "1rem"
    },
    shadows: {
      4: "0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 1px 10px 0 rgba(0, 0, 0, 0.12), 0 4px 5px 0 rgba(0, 0, 0, 0.14)",
      8: "0 5px 5px -3px rgba(0, 0, 0, 0.18), 0 3px 14px 2px rgba(0, 0, 0, 0.1), 0 8px 10px 1px rgba(0, 0, 0, 0.12)",
      16: "0 10px 10px -5px rgba(0, 0, 0, 0.16), 0 5px 30px 5px rgba(0, 0, 0, 0.06), 0 15px 24px 2px rgba(0, 0, 0, 0.1)"
    },
    space: {
      xxs: ".25rem",
      //4px
      xs: ".5rem",
      //8px
      s: "1rem",
      //16px
      m: "1.5rem",
      //24px
      l: "2rem",
      //32px
      xl: "3rem",
      //48px
      xxl: "5rem"
      //80px
    },
    borderWidths: {
      s: ".0625rem",
      m: ".125rem",
      l: ".25rem"
    },
    borderStyles: {
      primary: "1px solid #4d4e4c29",
      secondary: "1px solid #002b45"
    },
    fonts: {
      DFDS: "var(--font-base)",
      Verdana: "Verdana, system-ui, Arial, Helvetica Neue, Helvetica, sans-serif"
    },
    fontSizes: {
      "headline-1": "3rem",
      "headline-2": "2rem",
      //32px
      "headline-3": "1.5rem",
      //24px
      "headline-4": "1.25rem",
      //20px
      "headline-5": "1rem",
      //16px
      "headline-6": "0.875rem",
      //14px
      "subtitle-1": "1.25rem",
      //20px
      "subtitle-2": "0.875rem",
      //14px
      "body-1": "1rem",
      //
      "body-2": "0.875rem",
      //14px
      "label-small": "0.75rem",
      //12px
      "label-medium": "1rem",
      //16px
      caption: "0.75rem",
      //12px
      overline: "0.625rem"
      //10px
    },
    fontWeights: {
      light: 300,
      regular: 400,
      bold: 700
    },
    lineHeights: {
      "headline-1": 1.25,
      "headline-1s": 1.3,
      // variant 1 on small screen
      "headline-2": 1.25,
      "headline-2s": 1.43,
      // variant 2 on small screen
      "headline-3": 1.4,
      "headline-4": 1.4,
      "subtitle-1": 1.4,
      "subtitle-2": 1.428,
      "body-1": 1.5,
      "body-2": 1.25,
      "label-small": 1.334,
      "label-medium": 1.25,
      action: 1.25,
      caption: 1.333,
      overline: 1.6
    }
  },
  media: {
    mUp: `(min-width: ${breakpoints.m}px)`,
    lUp: `(min-width: ${breakpoints.l}px)`,
    xlUp: `(min-width: ${breakpoints.xl}px)`,
    xxlUp: `(min-width: ${breakpoints.xxl}px)`
  }
});
var pulse = keyframes({
  "0%": { opacity: 0.1 },
  "50%": { opacity: 0.2 },
  "100%": { opacity: 0.1 }
});
var sectionColors = {
  color: {
    WHITE: "$group-blue-primary",
    LIGHTER: "$group-blue-primary",
    LIGHT: "$group-blue-primary",
    DARK: "$white-primary",
    DARKER: "$white-primary"
  },
  backgroundColor: {
    WHITE: "$white-primary",
    LIGHTER: "$group-blue-07",
    LIGHT: "$group-blue-15",
    DARK: "$group-blue-70",
    DARKER: "$group-blue-primary"
  }
};
var whiteTheme = createTheme({
  colors: {
    text: "$group-blue-primary",
    background: "$white-primary",
    "button-filled-background": "$group-blue-primary",
    card: "#002b4514",
    overlay: "$group-blue-24",
    "surface-on-low-emphasis": "#002B451F"
  }
});
var darkerTheme = createTheme({
  colors: {
    text: "$white-primary",
    background: "$group-blue-primary",
    "button-filled-background": "$white-primary",
    card: "#ffffff14",
    overlay: "$white-secondary",
    "surface-on-low-emphasis": "#FFFFFF1F"
  }
});

// src/components/Button/Button.styles.ts
var NO_BORDER_SPACING = "12px";
var WITH_BORDER_SPACING = "10px";
var DefaultButtonStyles = {
  fontFamily: "$DFDS",
  position: "relative",
  lineHeight: "$label-medium",
  fontSize: "$body-1",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "pre-wrap",
  borderRadius: "$s",
  boxSizing: "border-box",
  textDecoration: "none",
  textAlign: "center",
  fontWeight: "$bold",
  overflow: "visible",
  color: "$text",
  backgroundColor: "transparent",
  border: "2px solid transparent",
  paddingInline: "$m",
  letterSpacing: "0.25px",
  cursor: "pointer",
  transition: "background-color 120ms ease, color 120ms ease",
  gap: "$xs",
  "&:after": {
    content: "",
    display: "block",
    top: "0",
    left: "0",
    height: "100%",
    width: "100%",
    position: "absolute",
    opacity: 0,
    transition: "opacity 120ms ease"
  },
  "&:hover:after": {
    opacity: 0.12
  },
  "&:focus:after": {
    opacity: 0.2
  },
  "&:active:after": {
    opacity: 0.2
  },
  "&:hover, &:focus, &:active, &:visited": {
    color: "$text"
  },
  "&:disabled, &[disabled]": {
    pointerEvents: "none",
    filter: "grayscale(1)",
    opacity: 0.5
  },
  variants: {
    variant: {
      filled: {
        "--colors-text": "var(--colors-background)",
        borderColor: "$button-filled-background",
        backgroundColor: "$button-filled-background",
        paddingBlock: NO_BORDER_SPACING,
        border: "none",
        "&:after": {
          backgroundColor: "$background"
        }
      },
      outlined: {
        borderColor: "$text",
        paddingBlock: WITH_BORDER_SPACING,
        "&:after": {
          backgroundColor: "$text"
        }
      },
      text: {
        borderColor: "transparent",
        backgroundColor: "transparent",
        paddingBlock: NO_BORDER_SPACING,
        border: "none",
        "&:after": {
          backgroundColor: "$text"
        }
      },
      flush: {
        padding: 0,
        border: "none",
        "&:after": {
          content: "none"
        },
        "&:hover, &:focus, &:active": {
          "--colors-text": theme.colors["navy-medium"]
        },
        "&:disabled, &[disabled]": {
          "--colors-text": theme.colors["blue-disabled"]
        }
      },
      cta: {
        "--colors-text": theme.colors["white-primary"],
        backgroundColor: "$orange-primary",
        paddingBlock: NO_BORDER_SPACING,
        border: "none",
        "&:after": {
          backgroundColor: "$white-primary"
        }
      }
    },
    round: {
      true: {
        borderRadius: "50%",
        padding: "$xs",
        "&:after": {
          borderRadius: "50%"
        }
      }
    },
    selected: {
      true: {
        borderColor: "$text"
      }
    }
  },
  defaultVariants: {
    variant: "outlined"
  }
};
var Button = styled("button", DefaultButtonStyles);

// src/components/Container/Container.styles.ts
var Container = styled("div", {
  margin: "0 $s",
  "@mUp": {
    margin: "0 $m",
    gridRowGap: "$m",
    gridColumnGap: "$m"
  },
  "@lUp": {
    margin: "0 40px",
    gridRowGap: "$l",
    gridColumnGap: "$l"
  },
  "@xlUp": {
    maxWidth: "1200px",
    margin: "auto"
  }
});

// src/components/Divider/Divider.styles.ts
var StyledDivider = styled("hr", {
  border: "none",
  maxWidth: "100%",
  height: "1px",
  backgroundColor: "$surface-on-low-emphasis",
  margin: 0,
  variants: {
    margins: {
      true: {
        margin: `0 $m`
      }
    },
    indent: {
      true: {
        margin: `0 0 0 $m`
      }
    }
  }
});
var Divider = (_a) => {
  var _b = _a, { css: css2 } = _b, rest = __objRest(_b, ["css"]);
  return /* @__PURE__ */ jsx(StyledDivider, __spreadValues({ role: "separator", css: __spreadValues({}, css2 || {}) }, rest));
};

// src/components/Grid/Grid.styles.ts
var GridOuterContainer = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  height: "auto",
  width: "100%",
  gridColumn: "span 12 / auto"
});
var GridInnerContainer = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  width: "100%",
  gridRowGap: "$s",
  gridColumnGap: "$s",
  margin: 0,
  "@mUp": {
    gridRowGap: "$m",
    gridColumnGap: "$m"
  },
  "@lUp": {
    gridRowGap: "$l",
    gridColumnGap: "$l"
  },
  "@xlUp": {
    margin: "0 auto",
    gridRowGap: "40px",
    gridColumnGap: "40px"
  },
  variants: {
    noMargin: {
      true: {
        margin: 0
      }
    }
  }
});
var Grid = (_a) => {
  var _b = _a, {
    children
  } = _b, rest = __objRest(_b, [
    "children"
  ]);
  return /* @__PURE__ */ jsx(GridOuterContainer, { children: /* @__PURE__ */ jsx(GridInnerContainer, __spreadProps(__spreadValues({}, rest), { children })) });
};
var StyledItem = styled("div", {});
var GridItem = (_a) => {
  var _b = _a, {
    css: css2,
    children,
    small = 12,
    medium,
    large,
    xlarge
  } = _b, rest = __objRest(_b, [
    "css",
    "children",
    "small",
    "medium",
    "large",
    "xlarge"
  ]);
  const mediumWidth = medium || small;
  const largeWidth = large || mediumWidth;
  const xLargeWidth = xlarge || largeWidth;
  return /* @__PURE__ */ jsx(
    StyledItem,
    __spreadProps(__spreadValues({
      css: __spreadValues({
        gridColumn: `span ${small}`,
        "@mUp": { gridColumn: `span ${mediumWidth}` },
        "@lUp": { gridColumn: `span ${largeWidth}` },
        "@xlUp": { gridColumn: `span ${xLargeWidth}` }
      }, css2 || {})
    }, rest), {
      children
    })
  );
};

// src/components/Link/Link.styles.ts
var DefaultLinkStyle = __spreadProps(__spreadValues({}, DefaultButtonStyles), {
  variants: __spreadProps(__spreadValues({}, DefaultButtonStyles.variants), {
    fullwidth: {
      true: {
        width: "100%",
        "@mUp": {
          width: "fit-content"
        }
      }
    }
  }),
  defaultVariants: {
    variant: "basic"
  }
});
var StyledLink = styled("a", DefaultLinkStyle);

// src/components/Skeleton/Skeleton.styles.tsx
var StyledSkeleton = styled("div", {
  animation: `${pulse} 2.8s infinite forwards`,
  position: "relative",
  backgroundColor: "$group-blue-primary",
  width: "100%",
  height: "90px",
  borderRadius: "$s",
  display: "inline-flex",
  lineHeight: "16px",
  marginBottom: "-4px"
});
var Skeleton = ({ children }) => {
  return /* @__PURE__ */ jsx(StyledSkeleton, { children });
};

// src/components/Text/Text.styles.ts
var Text = styled("p", {
  fontFamily: "$DFDS",
  color: "$text",
  margin: 0,
  variants: {
    variant: {
      "headline-1": {
        fontSize: "$headline-1",
        lineHeight: "$headline-1",
        fontWeight: "$light"
      },
      "headline-2": {
        fontSize: "$headline-2",
        lineHeight: "$headline-2",
        fontWeight: "$regular"
      },
      "headline-3": {
        fontSize: "$headline-3",
        lineHeight: "$headline-3",
        fontWeight: "$bold"
      },
      "headline-4": {
        fontSize: "$headline-4",
        lineHeight: "$headline-4",
        fontWeight: "$bold"
      },
      "headline-5": {
        fontSize: "$headline-5",
        lineHeight: "$headline-4",
        fontWeight: "$bold"
      },
      "headline-6": {
        fontSize: "$headline-6",
        lineHeight: "$headline-4",
        fontWeight: "$bold"
      },
      "subtitle-1": {
        fontSize: "$subtitle-1",
        lineHeight: "$subtitle-1",
        fontWeight: "$light"
      },
      "subtitle-2": {
        fontSize: "$subtitle-2",
        lineHeight: "$subtitle-2",
        fontWeight: "$bold"
      },
      "body-1": {
        fontSize: "$body-1",
        lineHeight: "$body-1",
        fontWeight: "$regular"
      },
      "body-1-bold": {
        fontSize: "$body-1",
        lineHeight: "$body-1",
        fontWeight: "$bold"
      },
      "body-1-light": {
        fontSize: "$body-1",
        lineHeight: "$body-1",
        fontWeight: "$light"
      },
      "body-2": {
        fontFamily: "$Verdana",
        fontSize: "$body-2",
        lineHeight: "$body-2",
        fontWeight: "$regular"
      },
      "body-2-bold": {
        fontFamily: "$Verdana",
        fontSize: "$body-2",
        lineHeight: "$body-2",
        fontWeight: "$bold"
      },
      "label-small": {
        fontSize: "$label-small",
        lineHeight: "$label-small"
      },
      "label-medium": {
        fontSize: "$label-medium",
        lineHeight: "$label-medium"
      },
      "label-medium-bold": {
        fontSize: "$label-medium",
        lineHeight: "$label-medium",
        fontWeight: "$bold"
      },
      caption: {
        fontSize: "$caption",
        lineHeight: "$caption"
      },
      overline: {
        fontSize: "$overline",
        lineHeight: "$overline"
      },
      action: {
        padding: "$xs",
        fontWeight: "$bold",
        fontSize: "$body-1"
      }
    }
  }
});
var Account = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx("svg", __spreadProps(__spreadValues({ width: "1em", height: "1em", viewBox: "0 0 24 24", ref: svgRef }, props), { children: /* @__PURE__ */ jsx(
    "path",
    {
      fill: "var(--colors-text)",
      d: "M12.453 14a10 10 0 0 1 9.417 6.637L22 21H2l.13-.363A10 10 0 0 1 11.547 14h.906zM12 3a5 5 0 1 1-.001 10.001A5 5 0 0 1 12 3z"
    }
  ) }))
);
Account.displayName = "Account";
var ArrowRight = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx(
    "svg",
    __spreadProps(__spreadValues({
      xmlns: "http://www.w3.org/2000/svg",
      width: "2rem",
      height: "2rem",
      viewBox: "0 0 25 24",
      ref: svgRef
    }, props), {
      children: /* @__PURE__ */ jsx(
        "path",
        {
          fill: "var(--colors-text)",
          d: "M16.5128 13L12.666 16.5611L14.033 18L19.5455 13H19.666V12.8907L20.666 11.9836L19.666 11.0599V11H19.6011L14.1883 6L12.7874 7.49814L16.5529 11L4.66599 11L4.66599 13L16.5128 13Z"
        }
      )
    })
  )
);
ArrowRight.displayName = "ArrowRight";
var BuFreightShipping = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsxs("svg", __spreadProps(__spreadValues({ width: "1em", height: "1em", viewBox: "0 0 24 24", ref: svgRef }, props), { children: [
    /* @__PURE__ */ jsx("path", { fill: "inherit", d: "M10 8v5H2V8h8zm10 0v5h-9V8h9zM10 2v5H2V2h8z" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        fill: "var(--colors-text)",
        opacity: 0.99,
        d: "M22 14l-2.833 6.765a2 2 0 0 1-1.844 1.227L2 21.999v-8h20zm-4.762 2c-.526 0-.952.448-.952 1s.426 1 .952 1 .952-.448.952-1-.426-1-.952-1zm-2.857 0c-.526 0-.952.448-.952 1s.426 1 .952 1 .952-.448.952-1-.426-1-.952-1zm-2.857 0c-.526 0-.952.448-.952 1s.426 1 .952 1 .952-.448.952-1-.426-1-.952-1z"
      }
    )
  ] }))
);
BuFreightShipping.displayName = "BuFreightShipping";
var ChevronDown = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx(
    "svg",
    __spreadProps(__spreadValues({
      width: "1.25rem",
      height: "1.25rem",
      viewBox: "0 0 24 24",
      ref: svgRef
    }, props), {
      children: /* @__PURE__ */ jsx(
        "path",
        {
          fill: "var(--colors-text)",
          d: "M7.41 8L12 12.945 16.59 8 18 9.522 12 16 6 9.522z"
        }
      )
    })
  )
);
ChevronDown.displayName = "ChevronDown";
var ChevronLeft = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx(
    "svg",
    __spreadProps(__spreadValues({
      width: "1.25rem",
      height: "1.25rem",
      viewBox: "0 0 24 24",
      ref: svgRef
    }, props), {
      children: /* @__PURE__ */ jsx(
        "path",
        {
          fill: "var(--colors-text)",
          d: "M15.41 16.09l-4.58-4.59 4.58-4.59L14 5.5l-6 6 6 6z"
        }
      )
    })
  )
);
ChevronLeft.displayName = "ChevronLeft";
var ChevronRight = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx(
    "svg",
    __spreadProps(__spreadValues({
      width: "1.25rem",
      height: "1.25rem",
      viewBox: "0 0 24 24",
      ref: svgRef
    }, props), {
      children: /* @__PURE__ */ jsx(
        "path",
        {
          fill: "var(--colors-text)",
          d: "M8.59 16.34l4.58-4.59-4.58-4.59L10 5.75l6 6-6 6z"
        }
      )
    })
  )
);
ChevronRight.displayName = "ChevronRight";
var Close = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx("svg", __spreadProps(__spreadValues({ width: "1em", height: "1em", viewBox: "0 0 24 24", ref: svgRef }, props), { children: /* @__PURE__ */ jsx(
    "path",
    {
      fill: "var(--colors-text)",
      d: "M4.929 17.657l5.658-5.656-5.658-5.658 1.414-1.414 5.658 5.658 5.656-5.658 1.414 1.414-5.656 5.658 5.656 5.656-1.414 1.414-5.656-5.656-5.658 5.656-1.414-1.414z"
    }
  ) }))
);
Close.displayName = "Close";
var ExternalLink = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx("svg", __spreadProps(__spreadValues({ width: "1em", height: "1em", viewBox: "0 0 24 24", ref: svgRef }, props), { children: /* @__PURE__ */ jsx(
    "path",
    {
      fill: "var(--colors-text)",
      d: "M11 4.999v2L6 7v12h12v-5.001h2V19a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2l5-.001zM21 4v7l-1.987-.054-.009-3.543-6.844 6.844-1.414-1.414 6.817-6.818-3.491.038L14 4h7z"
    }
  ) }))
);
ExternalLink.displayName = "ExternalLink";
var Information = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx("svg", __spreadProps(__spreadValues({ width: "1em", height: "1em", viewBox: "0 0 24 24", ref: svgRef }, props), { children: /* @__PURE__ */ jsx(
    "path",
    {
      fill: "var(--colors-text)",
      d: "M12.974 10.75c.103 0 .175.014.216.041s.06.077.06.148v5.871c0 .071-.02.12-.06.148s-.112.041-.216.041h-1.948c-.103 0-.175-.014-.216-.041s-.06-.077-.06-.148v-5.871c0-.071.02-.12.06-.148s.112-.041.216-.041h1.948zM13.013 7c.089 0 .15.022.185.065s.052.12.052.231v1.907c0 .111-.017.188-.052.231s-.096.065-.185.065h-2.027c-.089 0-.15-.022-.185-.065s-.052-.12-.052-.231V7.296c0-.111.017-.188.052-.231S10.897 7 10.986 7h2.027zM12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16z"
    }
  ) }))
);
Information.displayName = "Information";
var Menu = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx("svg", __spreadProps(__spreadValues({ width: "1em", height: "1em", viewBox: "0 0 22 22", ref: svgRef }, props), { children: /* @__PURE__ */ jsx(
    "path",
    {
      fill: "var(--colors-text)",
      d: "M21 16v2H3v-2h18zm0-5v2H3v-2h18zm0-5v2H3V6h18z"
    }
  ) }))
);
Menu.displayName = "Menu";
var Search = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx("svg", __spreadProps(__spreadValues({ width: "1em", height: "1em", viewBox: "0 0 22 22", ref: svgRef }, props), { children: /* @__PURE__ */ jsx(
    "path",
    {
      fill: "var(--colors-text)",
      d: "M9.414 3a6.414 6.414 0 0 1 5.18 10.198l6.407 6.408-1.395 1.395-6.408-6.407A6.414 6.414 0 1 1 9.414 3zm0 1.973a4.44 4.44 0 1 0 0 8.88 4.44 4.44 0 0 0 0-8.88z"
    }
  ) }))
);
Search.displayName = "Search";
var SvgDfdsLogo = forwardRef(
  (_a, svgRef) => {
    var _b = _a, { title } = _b, props = __objRest(_b, ["title"]);
    return /* @__PURE__ */ jsxs("svg", __spreadProps(__spreadValues({ viewBox: "0 0 145 50", ref: svgRef }, props), { children: [
      title !== void 0 && /* @__PURE__ */ jsx("title", { children: title }),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "m140.2 23.58-5.1-1.69c-1.15-.36-1.45-.59-1.45-1.81v-.46c0-1.65.63-1.65 2.17-1.65 3.26 0 7 .1 7 .1.39 0 .53-.13.53-.53V14c0-.4-.14-.53-.53-.53 0 0-4.12-.1-7.71-.1-4.08 0-6.45 1.76-6.45 5.57v1.78c0 4.12 2.34 4.82 4.54 5.51l5.07 1.65c1.16.36 1.45.69 1.45 1.91v.53c0 1.65-.59 1.76-2.14 1.76-3.51 0-8.21-.1-8.21-.1-.4 0-.53.13-.53.53v3.59c0 .4.13.53.53.53 0 0 4.79.1 8.9.1s6.44-1.93 6.44-5.75v-1.86c.03-4.12-2.33-4.85-4.51-5.54zM119.67 13.49H109c-.39 0-.53.13-.53.53V36c0 .4.14.53.53.53h10.63c4.09 0 6.46-2.27 6.46-6.1V19.59c.04-3.83-2.33-6.1-6.42-6.1zm1.49 16.06c0 1.64-.63 2.3-2.18 2.3h-5.49V18.14H119c1.55 0 2.18.65 2.18 2.31zM105.3 13.49H91.18c-.4 0-.53.13-.53.53V36c0 .4.13.53.53.53h3.92c.39 0 .52-.13.52-.53v-8.23h8.46c.4 0 .53-.13.53-.52v-3.61c0-.39-.13-.52-.53-.52h-8.46v-5h9.68c.4 0 .53-.14.53-.53V14c0-.38-.13-.51-.53-.51zM81 13.49H70.32c-.39 0-.53.13-.53.53V36c0 .4.14.53.53.53H81c4.08 0 6.45-2.27 6.45-6.1V19.59c-.04-3.83-2.45-6.1-6.45-6.1zm1.48 16.06c0 1.64-.62 2.3-2.17 2.3h-5.54V18.14h5.49c1.55 0 2.17.65 2.17 2.31zM69.4 0H29.18a.9.9 0 0 0-.85.5L.07 49.46a.33.33 0 0 0 .06.47.32.32 0 0 0 .27.07h40.2a.89.89 0 0 0 .86-.49L69.72.54a.33.33 0 0 0-.07-.47.32.32 0 0 0-.25-.07zm-23 32a.28.28 0 0 1-.27.28.42.42 0 0 1-.15 0l-9.2-5.31 5.31 9.2a.28.28 0 0 1-.1.38.33.33 0 0 1-.14 0H27.94a.28.28 0 0 1-.28-.28.23.23 0 0 1 0-.14l5.3-9.2-9.17 5.27a.28.28 0 0 1-.38-.11.31.31 0 0 1 0-.13V18a.27.27 0 0 1 .28-.27.28.28 0 0 1 .13 0L33 23.11l-5.31-9.2a.28.28 0 0 1 .1-.38.33.33 0 0 1 .14 0h13.91a.29.29 0 0 1 .28.28.23.23 0 0 1 0 .14l-5.31 9.2L46 17.8a.27.27 0 0 1 .38.1.23.23 0 0 1 0 .14z",
          fill: theme.colors["group-blue-primary"].value
        }
      )
    ] }));
  }
);
SvgDfdsLogo.displayName = "SvgDfdsLogo";
var SvgDocument = forwardRef(
  (props, svgRef) => /* @__PURE__ */ jsx("svg", __spreadProps(__spreadValues({ width: "1em", height: "1em", viewBox: "0 0 24 24", ref: svgRef }, props), { children: /* @__PURE__ */ jsx(
    "path",
    {
      fill: "var(--colors-text)",
      d: "M15 2l5 6v12c0 1-1 2-2 2H6c-1.105 0-2-1-2-2V4c0-1 .895-2 2-2h9zm1 14v2H8v-2h8zm0-3v2H8v-2h8zm0-3v2H8v-2h8zm-2-2V4H6v16h12V8h-4z"
    }
  ) }))
);
SvgDocument.displayName = "Document";

export { Account, ArrowRight, BuFreightShipping, Button, ChevronDown, ChevronLeft, ChevronRight, Close, Container, DefaultButtonStyles, DefaultLinkStyle, Divider, ExternalLink, Grid, GridItem, Information, StyledLink as Link, Menu, Search, Skeleton, StyledItem, StyledSkeleton, SvgDfdsLogo, SvgDocument, Text, breakpoints, config, createTheme, css, darkerTheme, getCssText, globalCss, keyframes, pulse, sectionColors, styled, theme, whiteTheme };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.js.map